import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_EXCHANGE_RATES,
  clearManualExchangeRate,
  formatExchangeRatesForDisplay,
  getManualExchangeOverrides,
  refreshExchangeRates,
  setManualExchangeRate,
  subscribeExchangeRates
} from '../utils/exchangeRates';

const buildSnapshot = (rates, formatForDisplay) => ({
  rates: formatForDisplay ? formatExchangeRatesForDisplay(rates) : rates,
  manual: getManualExchangeOverrides(),
  version: Date.now()
});

export const useExchangeRates = ({ formatForDisplay = false, includeMeta = false } = {}) => {
  const [snapshot, setSnapshot] = useState(() => buildSnapshot(DEFAULT_EXCHANGE_RATES, formatForDisplay));

  useEffect(() => {
    return subscribeExchangeRates((rates) => {
      setSnapshot(buildSnapshot(rates, formatForDisplay));
    });
  }, [formatForDisplay]);

  const applyManualRate = useCallback((currency, value) => {
    const rates = setManualExchangeRate(currency, value);
    setSnapshot(buildSnapshot(rates, formatForDisplay));
  }, [formatForDisplay]);

  const resetManualRate = useCallback((currency) => {
    const rates = clearManualExchangeRate(currency);
    setSnapshot(buildSnapshot(rates, formatForDisplay));
  }, [formatForDisplay]);

  const refreshRates = useCallback(async () => {
    const rates = await refreshExchangeRates({ preserveManual: false });
    setSnapshot(buildSnapshot(rates, formatForDisplay));
    return rates;
  }, [formatForDisplay]);

  if (includeMeta) {
    return {
      exchangeRates: snapshot.rates,
      manualOverrides: snapshot.manual,
      ratesVersion: snapshot.version,
      setManualRate: applyManualRate,
      resetManualRate,
      refreshRates
    };
  }

  return snapshot.rates;
};
